import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import gmbApi from '../../services/gmb';
import permissionsApi from '../../services/permissions';
import {
  CarteLigne, Feuille, Fiche, LienOrdinateur, Liste, Pill, Puces, Recherche,
  dateFr, depuis, messageErreur, normaliser, useFicheOuverte, useToast,
} from './MobileUI';
import './AvisMobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Avis Google — version téléphone (2026-10-24).
//
// Le cas d'usage : un responsable de site voit tomber un mauvais avis et y
// répond dans la journée depuis son téléphone. Il arrive sur « À répondre »,
// limité aux 30 derniers jours : 3 600 des 3 700 avis sans réponse ont plus
// d'un mois et noyaient l'urgent (on peut les rappeler en bas de la file).
// « 1-2 ★ » isole les mauvais avis sans réponse, en alerte.
//
// Mêmes routes et mêmes règles que GoogleAvis.js :
//   - GET /gmb/reviews (cloisonné par concession côté serveur, 1 000 avis au
//     plus, les plus récents d'abord) et GET /gmb/locations pour la feuille
//     « Fiche » (même cloisonnement) ;
//   - ouvrir un avis le marque lu (POST /reviews/:id/read) ;
//   - « 🪄 Brouillon IA » : POST /reviews/:id/draft. Contrairement à
//     l'ordinateur, il n'écrase pas le texte saisi sans retour : « ↶ Restaurer
//     mon texte » le rend ;
//   - « Publier sur Google » : POST /reviews/:id/reply, après une feuille de
//     confirmation qui montre le texte final et la fiche (publication publique
//     et immédiate : rien ne part sur un faux geste). Droit « modifier ».
// Reste sur ordinateur : tableau de bord, fiches, prompts IA, configuration,
// synchronisation, suppression.
// ═══════════════════════════════════════════════════════════════════════════

const FENETRE_JOURS = 30;
const MS_JOUR = 86400000;

// Les avis Google arrivent parfois sous la forme « (Translated by Google) …
// (Original) … » : on ne garde que le texte d'origine (même règle que
// l'ordinateur).
function texteOriginal(t) {
  if (!t) return t;
  const m = t.match(/\((?:Original|Avis d'origine)\)\s*([\s\S]*)$/i);
  if (m) return m[1].trim();
  return t.replace(/^\s*\((?:Translated by Google|Traduit par Google)\)\s*/i, '').trim();
}

const nomFiche = (r) => r.location_alias || r.location_title || 'Fiche inconnue';
const nomFicheLoc = (l) => l.alias || l.title || `Fiche ${l.id}`;
const estRepondu = (r) => r.reply_status === 'sent';
const estRecent = (r, maintenant) => r.create_time && maintenant - new Date(r.create_time).getTime() <= FENETRE_JOURS * MS_JOUR;

function Etoiles({ note, grande }) {
  const n = Math.max(0, Math.min(5, Number(note) || 0));
  return (
    <span className={`mb-avis-etoiles${n <= 2 ? ' mb-avis-etoiles--bas' : ''}${grande ? ' mb-avis-etoiles--grande' : ''}`}
      aria-label={`${n} étoile${n > 1 ? 's' : ''} sur 5`}>
      {'★'.repeat(n)}<span className="mb-avis-etoiles-vides">{'★'.repeat(5 - n)}</span>
    </span>
  );
}

function groupeDate(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return 'Plus ancien';
  const auj = new Date(); auj.setHours(0, 0, 0, 0);
  const jour = new Date(d); jour.setHours(0, 0, 0, 0);
  const ecart = Math.round((auj - jour) / MS_JOUR);
  if (ecart <= 0) return 'Aujourd’hui';
  if (ecart === 1) return 'Hier';
  if (ecart < 7) return 'Cette semaine';
  if (ecart < 31) return 'Ce mois-ci';
  return 'Plus d’un mois';
}

export default function AvisMobile({ user, onVersionOrdinateur }) {
  // Concession du menu (2026-10-26) : avis et fiches limités à elle, comme sur ordinateur.
  const concessionMenu = user?.concession_id ? String(user.concession_id) : '';
  const [toutes, setToutes] = useState([]);      // les 1 000 avis les plus récents
  const [sansReponse, setSansReponse] = useState([]); // les 1 000 sans réponse les plus récents
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [fiches, setFiches] = useState([]);
  const [ficheId, setFicheId] = useState('');
  const [feuilleFiche, setFeuilleFiche] = useState(false);
  const [file, setFile] = useState('todo');
  const [anciens, setAnciens] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [peutRepondre, setPeutRepondre] = useState(false);
  const [toast, afficher] = useToast();
  // Texte en cours par avis : passer à l'avis voisin (‹ ›) ne le perd pas.
  const saisies = useRef({});

  // Deux lectures : la file « sans réponse » a sa propre limite de 1 000,
  // sinon les avis répondus la rogneraient dès qu'on remonte au-delà d'un mois.
  const charger = useCallback(async () => {
    try {
      const params = {};
      if (concessionMenu) params.concession_id = concessionMenu;
      if (ficheId) params.location_id = ficheId;
      if (recherche.trim()) params.q = recherche.trim();
      const [a, b] = await Promise.all([gmbApi.getReviews(params), gmbApi.getReviews({ ...params, status: 'todo' })]);
      setToutes(a.data.reviews || []);
      setSansReponse(b.data.reviews || []);
      setErreur('');
    } catch (e) {
      setErreur(messageErreur(e, 'Les avis n’ont pas pu être chargés.'));
    } finally {
      setChargement(false);
    }
  }, [ficheId, recherche, concessionMenu]);

  useEffect(() => { setChargement(true); charger(); }, [charger]);
  useEffect(() => {
    gmbApi.getLocations()
      .then((r) => setFiches((r.data.locations || []).filter((l) => !concessionMenu || String(l.concession_id) === concessionMenu)))
      .catch(() => setFiches([]));
    permissionsApi.checkAccess('gmb_reviews')
      .then((r) => setPeutRepondre(!!r.data?.permissions?.peut_modifier))
      .catch(() => setPeutRepondre(false));
  }, [concessionMenu]);

  // Pas de flux temps réel : la liste se recharge au retour sur l'onglet.
  useEffect(() => {
    const auRetour = () => { if (document.visibilityState === 'visible') charger(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => document.removeEventListener('visibilitychange', auRetour);
  }, [charger]);

  // « Maintenant » figé au chargement : la fenêtre de 30 jours ne bouge pas
  // entre deux rendus.
  const maintenant = useMemo(() => Date.now(), [sansReponse]); // eslint-disable-line react-hooks/exhaustive-deps
  const sansReponseVisibles = useMemo(
    () => (anciens ? sansReponse : sansReponse.filter((r) => estRecent(r, maintenant))),
    [sansReponse, anciens, maintenant]
  );
  const nbAnciens = sansReponse.length - sansReponse.filter((r) => estRecent(r, maintenant)).length;

  const comptes = {
    todo: sansReponseVisibles.length,
    bas: sansReponseVisibles.filter((r) => Number(r.rating) <= 2).length,
    repondus: toutes.filter(estRepondu).length,
    tous: toutes.length,
  };

  const visibles = useMemo(() => {
    if (file === 'todo') return sansReponseVisibles;
    if (file === 'bas') return sansReponseVisibles.filter((r) => Number(r.rating) <= 2);
    if (file === 'repondus') return toutes.filter(estRepondu);
    return toutes;
  }, [file, sansReponseVisibles, toutes]);

  const cle = useCallback((r) => r.id, []);
  const fiche = useFicheOuverte(visibles, cle);

  const majLocale = (id, champs) => {
    const maj = (l) => l.map((r) => (r.id === id ? { ...r, ...champs } : r));
    setToutes(maj); setSansReponse(maj);
  };

  // Ouvrir un avis le marque lu, comme sur ordinateur.
  const ouverte = fiche.ouverte;
  useEffect(() => {
    if (!ouverte || ouverte.read_at) return;
    gmbApi.markRead(ouverte.id).catch(() => {});
    majLocale(ouverte.id, { read_at: new Date().toISOString() });
  }, [ouverte]); // eslint-disable-line react-hooks/exhaustive-deps

  const publie = (avis) => {
    delete saisies.current[avis.id];
    // On passe à l'avis suivant de la file plutôt que de revenir à la liste.
    if (fiche.suivant) fiche.suivant(); else if (fiche.precedent) fiche.precedent(); else fiche.fermer();
    majLocale(avis.id, avis);
    setSansReponse((l) => l.filter((r) => r.id !== avis.id));
    afficher('Réponse publiée sur Google');
  };

  const ficheChoisie = fiches.find((l) => String(l.id) === String(ficheId));
  const fenetre = anciens ? '' : ` · ${FENETRE_JOURS} j`;

  return (
    <div className="mb-page">
      <div className="mb-outils">
        <Recherche id="mb-avis-q" valeur={recherche} onChange={setRecherche} delai={400} placeholder="Auteur ou mot de l’avis" />
        {fiches.length > 1 && (
          <button type="button" className={`mb-avis-filtre${ficheId ? ' is-actif' : ''}`} onClick={() => setFeuilleFiche(true)}>
            <span>{ficheChoisie ? nomFicheLoc(ficheChoisie) : 'Toutes les fiches'}</span>
            <span aria-hidden="true">▾</span>
          </button>
        )}
      </div>
      <Puces label="File" valeur={file} onChange={setFile} options={[
        { cle: 'todo', libelle: `À répondre${fenetre}`, compte: comptes.todo },
        { cle: 'bas', libelle: '1-2 ★', compte: comptes.bas, alerte: true },
        { cle: 'repondus', libelle: 'Répondus', compte: comptes.repondus },
        { cle: 'tous', libelle: 'Tous', compte: comptes.tous },
      ]} />

      <Liste
        elements={visibles}
        cle={cle}
        chargement={chargement}
        erreur={erreur}
        vide={file === 'todo' ? `Aucun avis sans réponse${anciens ? '' : ` sur les ${FENETRE_JOURS} derniers jours`}.`
          : file === 'bas' ? `Aucun avis à 1 ou 2 étoiles sans réponse${anciens ? '' : ` sur les ${FENETRE_JOURS} derniers jours`}.`
            : 'Aucun avis pour ces filtres.'}
        groupe={(r) => groupeDate(r.create_time)}
        onOuvrir={fiche.ouvrir}
        carte={(r) => {
          const repondu = estRepondu(r);
          const bas = Number(r.rating) <= 2;
          return {
            nonLu: !r.read_at,
            couleur: repondu ? 'transparent' : bas ? 'var(--color-error)' : 'var(--color-warning)',
            contenu: (
              <>
                <CarteLigne avant={<Etoiles note={r.rating} />} titre={r.author_name || 'Anonyme'} age={depuis(r.create_time)} />
                <span className={`mb-carte-texte mb-carte-texte--3${r.comment ? '' : ' mb-avis-sans-texte'}`}>
                  {texteOriginal(r.comment) || '(note sans commentaire)'}
                </span>
                <span className="mb-carte-meta">
                  <Pill>{nomFiche(r)}</Pill>
                  {repondu ? <Pill ton="ok">Répondu</Pill> : <Pill ton={bas ? 'ko' : 'attente'}>À répondre</Pill>}
                </span>
              </>
            ),
          };
        }}
      />
      {(file === 'todo' || file === 'bas') && !chargement && (anciens || nbAnciens > 0) && (
        <button type="button" className="mb-lien-ordinateur" onClick={() => setAnciens((v) => !v)}>
          {anciens ? `Revenir aux ${FENETRE_JOURS} derniers jours`
            : `Voir aussi les ${nbAnciens} avis sans réponse de plus de ${FENETRE_JOURS} jours`}
        </button>
      )}
      <LienOrdinateur onClick={onVersionOrdinateur}>Tableau de bord, fiches, prompts IA : version ordinateur</LienOrdinateur>

      {fiche.ouverte && (
        <FicheAvis
          key={fiche.ouverte.id}
          avis={fiche.ouverte}
          position={fiche.index}
          total={visibles.length}
          onRetour={fiche.fermer}
          onPrecedent={fiche.precedent}
          onSuivant={fiche.suivant}
          peutRepondre={peutRepondre}
          saisies={saisies}
          onPublie={publie}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}

      {feuilleFiche && (
        <FeuilleFiche fiches={fiches} actuelle={ficheId}
          onChoisir={(id) => { setFicheId(id); setFeuilleFiche(false); }}
          onFermer={() => setFeuilleFiche(false)} />
      )}
      {toast}
    </div>
  );
}

function FicheAvis({ avis, position, total, onRetour, onPrecedent, onSuivant, peutRepondre, saisies, onPublie, onVersionOrdinateur }) {
  const [texte, setTexteBrut] = useState(() => saisies.current[avis.id] ?? avis.reply_comment ?? '');
  const [precedent, setPrecedent] = useState(null); // texte d'avant le brouillon IA
  const [redaction, setRedaction] = useState(false);
  const [confirmer, setConfirmer] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const setTexte = (t) => { setTexteBrut(t); saisies.current[avis.id] = t; };

  const brouillon = async () => {
    setRedaction(true); setErreur('');
    try {
      const { data } = await gmbApi.draftReply(avis.id);
      if (data.draft) {
        // Le texte saisi n'est pas perdu : « Restaurer mon texte » le rend.
        if (texte.trim() && texte !== data.draft) setPrecedent(texte);
        setTexte(data.draft);
      }
    } catch (e) {
      setErreur(messageErreur(e, 'Génération IA indisponible — rédigez manuellement.'));
    } finally {
      setRedaction(false);
    }
  };
  const restaurer = () => { setTexte(precedent); setPrecedent(null); };

  const publier = async () => {
    if (!texte.trim() || envoi) return;
    setEnvoi(true); setErreur('');
    try {
      const { data } = await gmbApi.reply(avis.id, texte.trim());
      onPublie({ ...avis, ...(data.review || {}), reply_status: 'sent', reply_comment: texte.trim() });
    } catch (e) {
      setErreur(messageErreur(e, 'Échec de la publication sur Google.'));
      setEnvoi(false);
    }
  };

  const repondu = estRepondu(avis);
  const inchange = repondu && texte.trim() === String(avis.reply_comment || '').trim();
  const pret = !!texte.trim() && !inchange;

  return (
    <Fiche
      titre={nomFiche(avis)}
      label={`Avis de ${avis.author_name || 'Anonyme'}`}
      position={position}
      total={total}
      onRetour={onRetour}
      onPrecedent={onPrecedent}
      onSuivant={onSuivant}
      pied={peutRepondre && (
        <button type="button" className="mb-bouton mb-bouton--principal" disabled={!pret || redaction} onClick={() => { setErreur(''); setConfirmer(true); }}>
          {repondu ? 'Mettre à jour sur Google' : 'Publier sur Google'}
        </button>
      )}
    >
      <div className="mb-entete">
        <div className="mb-entete-haut">
          <Etoiles note={avis.rating} grande />
          <span className="mb-avis-quand">{depuis(avis.create_time)} · {dateFr(avis.create_time)}</span>
        </div>
        <div>
          <div className="mb-entete-titre">{avis.author_name || 'Anonyme'}</div>
          <div className="mb-entete-sous">{nomFiche(avis)}{avis.concession_nom ? ` · ${avis.concession_nom}` : ' · concession non rattachée'}</div>
        </div>
        <div className={`mb-citation${avis.comment ? '' : ' mb-avis-sans-texte'}`}>
          {texteOriginal(avis.comment) || '(note sans commentaire)'}
        </div>
      </div>

      {repondu && avis.reply_comment && (
        <div className="mb-citation mb-citation--reponse">
          {avis.reply_comment}
          <small>Réponse publiée{avis.reply_update_time ? ` le ${dateFr(avis.reply_update_time)}` : ''}</small>
        </div>
      )}

      {peutRepondre ? (
        <div className="mb-carton">
          <b>{repondu ? 'Modifier la réponse' : 'Votre réponse publique'}</b>
          <textarea id="mb-avis-reponse" className="mb-zone" value={texte} onChange={(e) => setTexte(e.target.value)}
            placeholder="Écrivez votre réponse, ou demandez un brouillon à l’IA." aria-label="Votre réponse publique" />
          <div className="mb-rangee">
            <button type="button" className="mb-mini mb-mini--accent" onClick={brouillon} disabled={redaction || envoi}>
              {redaction ? '… génération' : '🪄 Brouillon IA'}
            </button>
            {precedent !== null && (
              <button type="button" className="mb-mini" onClick={restaurer} disabled={redaction}>↶ Restaurer mon texte</button>
            )}
            <span className="mb-compteur">{texte.trim().length} car.</span>
          </div>
          {precedent !== null && <p className="mb-aide">Brouillon proposé par l’IA. Votre texte est gardé : « Restaurer mon texte » le rend.</p>}
          {erreur && !confirmer && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
        </div>
      ) : (
        <p className="mb-aide mb-aide--centre">Lecture seule : répondre aux avis demande le droit « modifier » sur cette page.</p>
      )}

      <p className="mb-aide mb-aide--centre">Tableau de bord, fiches, prompts IA : version ordinateur.</p>
      <LienOrdinateur onClick={onVersionOrdinateur} />

      {confirmer && (
        <Feuille titre={repondu ? 'Mettre à jour la réponse ?' : 'Publier la réponse ?'} onFermer={() => setConfirmer(false)} fermer="Relire">
          <p className="mb-aide">
            Elle sera visible par tout le monde sur la fiche <b>{nomFiche(avis)}</b>, sous l’avis de <b>{avis.author_name || 'Anonyme'}</b>.
            {repondu && ' Elle remplace la réponse déjà publiée.'}
          </p>
          <div className="mb-citation mb-avis-apercu">{texte.trim()}</div>
          {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
          <div className="mb-feuille-pied">
            <button type="button" className="mb-bouton" onClick={() => setConfirmer(false)} disabled={envoi}>Relire</button>
            <button type="button" className="mb-bouton mb-bouton--principal" onClick={publier} disabled={envoi}>
              {envoi ? 'Publication…' : 'Publier'}
            </button>
          </div>
        </Feuille>
      )}
    </Fiche>
  );
}

// Choix de la fiche Google, regroupées par concession (la liste vient du
// serveur, déjà cloisonnée : un opérateur n'y voit que les siennes).
function FeuilleFiche({ fiches, actuelle, onChoisir, onFermer }) {
  const [q, setQ] = useState('');
  const qn = normaliser(q);
  const liste = fiches
    .filter((l) => !qn || normaliser(`${nomFicheLoc(l)} ${l.title || ''} ${l.concession_nom || ''}`).includes(qn))
    // Les fiches non rattachées à une concession en dernier.
    .sort((a, b) => (Number(!a.concession_nom) - Number(!b.concession_nom))
      || String(a.concession_nom || '').localeCompare(String(b.concession_nom || ''), 'fr')
      || nomFicheLoc(a).localeCompare(nomFicheLoc(b), 'fr'));
  const sortie = [];
  let courant;
  liste.forEach((l) => {
    const g = l.concession_nom || 'Sans concession';
    if (g !== courant) { courant = g; sortie.push(<div key={`g-${g}`} className="mb-groupe"><span>{g}</span></div>); }
    sortie.push(
      <button key={l.id} type="button" className={`mb-option${String(l.id) === String(actuelle) ? ' is-actif' : ''}`} onClick={() => onChoisir(String(l.id))}>
        {nomFicheLoc(l)}{l.alias && l.title && l.alias !== l.title && <small>{l.title}</small>}
      </button>
    );
  });
  return (
    <Feuille titre="Fiche Google" onFermer={onFermer}>
      {/* Enveloppe à hauteur fixe : dans le corps de la feuille (colonne flex
          qui défile), le champ seul se tasse quand la liste déborde. */}
      <div className="mb-avis-fixe">
        <Recherche id="mb-avis-fiche-q" valeur={q} onChange={setQ} placeholder="Nom de la fiche ou concession" />
      </div>
      <div className="mb-options">
        <button type="button" className={`mb-option${!actuelle ? ' is-actif' : ''}`} onClick={() => onChoisir('')}>Toutes les fiches</button>
        {sortie}
        {!liste.length && <p className="mb-aide">Aucune fiche ne correspond.</p>}
      </div>
    </Feuille>
  );
}
